import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Form from 'components/elements/Form/form';
import CreatePasswordMessage from 'pages/PasswordResetMessage';
import { useParams } from 'react-router-dom';
import ResetPasswordService from 'services/hooks/reset-password-service';
import Input from 'components/elements/Form/Input/input';
import { useMutation, useQueryClient } from 'react-query';
import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { colours } from 'utils/constants';

interface ResetPasswordInterface {
  newPassword: string;
  confirmPassword: string;
}
// validation
const LoginSchema = yup.object().shape({
  newPassword: yup
    .string()
    .required('New Password is required')
    .min(8, 'Must be at least 8 characters')
    .matches(/[A-Z]/, 'Must include at least one uppercase letter')
    .matches(/[a-z]/, 'Must include at least one lowercase letter')
    .matches(/[0-9]/, 'Must include at least one number'),
  confirmPassword: yup
    .string()
    .required('Confirm Password is required')
    .oneOf([yup.ref('newPassword'), null], 'Passwords must match')
});

function CreatePasswordForm() {
  const { token } = useParams();
  const methods = useForm({ resolver: yupResolver(LoginSchema) });
  const [isMinCharValid, setIsMinCharValid] = useState(false);
  const [isUpperCaseValid, setIsUpperCaseValid] = useState(false);
  const [isLowerCaseValid, setIsLowerCaseValid] = useState(false);
  const [isNumberValid, setIsNumberValid] = useState(false);
  const [complete, setComplete] = React.useState(false);
  const [error, setError] = React.useState('');
  const queryClient = useQueryClient();

  const handlePasswordChange = (event: { target: { value: any } }) => {
    const { value } = event.target;
    setIsMinCharValid(value.length >= 8);
    setIsUpperCaseValid(/[A-Z]/.test(value));
    setIsLowerCaseValid(/[a-z]/.test(value));
    setIsNumberValid(/[0-9]/.test(value));
  };

  const { mutate, isLoading } = useMutation(ResetPasswordService.passwordResetConfirm, {
    onSuccess: () => {
      setComplete(true);
    },
    onError: () => {
      setError('Something went wrong. Please try again or contact support if the issue persists.');
    },
    onSettled: () => {
      queryClient.invalidateQueries('create');
    }
  });

  const onSubmit = async (data: ResetPasswordInterface) => {
    if (token) {
      const userCreatePassword = {
        token: token,
        newPassword: data.newPassword
      };
      mutate(userCreatePassword);
    }
  };

  const PasswordCriterion = ({ isValid, text }: any) => (
    <p className="text-neutral8 text-sm mb-2">
      {isValid ? (
        <CheckCircleIcon style={{ color: colours.seafoam, fontSize: '1.1rem' }} className="icon" />
      ) : (
        <CancelIcon style={{ color: colours.invalidGrey, fontSize: '1.1rem' }} className="icon" />
      )}
      <span className="pl-1.5">{text}</span>
    </p>
  );

  if (!complete) {
    return isLoading ? (
      <LoadingSpinner />
    ) : (
      <Form
        buttonLabel="Create Password"
        methods={methods}
        onSubmit={onSubmit}
        styles="text-left"
        customStyles={'!pb-8'}
      >
        <p>Enter a new password below to create your log in credentials.</p>
        <Input
          label="NEW PASSWORD"
          name="newPassword"
          type="password"
          placeholder="*****"
          autoFocus
          onChange={handlePasswordChange}
          labelClass="!font-normal"
        />
        <Input
          label="CONFIRM NEW PASSWORD"
          name="confirmPassword"
          type="password"
          placeholder="*****"
          labelClass="!font-normal"
        />
        <div className="pt-4 text-left leading-3">
          <div id="password-criteria">
            <PasswordCriterion isValid={isMinCharValid} text="Must be at least 8 characters" />
            <PasswordCriterion
              isValid={isUpperCaseValid}
              text="Must include at least one uppercase letter"
            />
            <PasswordCriterion
              isValid={isLowerCaseValid}
              text="Must include at least one lowercase letter"
            />
            <PasswordCriterion isValid={isNumberValid} text="Must include at least one number" />
          </div>
        </div>
        {error && <div className="flex text-secondary2">{error}</div>}
      </Form>
    );
  } else {
    return (
      <CreatePasswordMessage message="Password has been created successfully. Please log in." />
    );
  }
}

export default CreatePasswordForm;
